// src/components/shared/ExitRouteGuard/ExitRouteGuard.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  checkEligibility,
  fetchExitStatusOnly,
} from "../../../redux/slices/exitFlowSlice";

/**
 * Map server states to the page we want to be on
 * tweak mapping as your product requires
 */
export const targetForState = (state, currentPath = "") => {
  switch (state) {
    case "not_started":
      // If the user explicitly opened phase-1 (/gameFinish), let them stay there to initiate.
      // Otherwise keep them in gameplay.
      return currentPath === "/gameFinish" ? "/gameFinish" : "/map";
    case "reveal_done":
    case "reward_ready":
    case "finalized":
      return "/finals";
    case "initiated":
    case "montage_done":
    case "details_submitted":
    case "reveal_ready":
      return "/gameFinishP2";
    default:
      return "/map";
  }
};

export default function ExitRouteGuard({ children }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  // read creds from store
  const { systemName, authToken } = useSelector(
    (s) => ({
      systemName: s.createUser?.user?.systemName,
      authToken: s.createUser?.user?.authToken,
    }),
    shallowEqual
  );

  // subscribe to the authoritative exit state in redux
  const exitState = useSelector((s) => s.exitFlow?.state);

  // local flags for initial sync
  const [initialSyncing, setInitialSyncing] = useState(true);
  const [initialTarget, setInitialTarget] = useState("/map");

  // 1) Initial (one-time) eligibility + status check (NO initiation)
  useEffect(() => {
    let cancelled = false;

    const runInitial = async () => {
      // if no creds, bounce out
      if (!systemName || !authToken) {
        if (!cancelled) {
          setInitialTarget("/login");
          setInitialSyncing(false);
        }
        return;
      }

      try {
        const eligAction = await dispatch(
          checkEligibility({ systemName, authToken })
        );

        // if explicitly not eligible, go map
        if (
          checkEligibility.rejected.match(eligAction) &&
          eligAction.payload?.code === "NOT_ELIGIBLE"
        ) {
          if (!cancelled) {
            setInitialTarget("/map");
            setInitialSyncing(false);
          }
          return;
        }

        const statusAction = await dispatch(
          fetchExitStatusOnly({ systemName, authToken })
        );

        const nextState = statusAction.payload?.state;
        const dest = targetForState(nextState, location.pathname) || "/map";
        if (!cancelled) {
          setInitialTarget(dest);
          setInitialSyncing(false);
        }
      } catch (err) {
        if (!cancelled) {
          setInitialTarget("/map");
          setInitialSyncing(false);
        }
      }
    };

    runInitial();

    return () => {
      cancelled = true;
    };
  }, [dispatch, systemName, authToken, location.pathname]);

  // 2) React to changes in exitState — navigate to the right page when it changes.
  useEffect(() => {
    // don't auto-redirect until we've completed the initial sync:
    if (initialSyncing) return;

    const dest = targetForState(exitState, location.pathname) || "/map";
    // only navigate if we are not already there
    if (dest && location.pathname !== dest) {
      // use replace to avoid backstack issues
      navigate(dest, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exitState, initialSyncing, location.pathname]); // intentionally only depends on exitState + initialSyncing

  // while we haven't finished the first sync, show a lightweight loader
  if (initialSyncing) {
    return <div>Syncing exit status…</div>;
  }

  // If the initial sync says we should be somewhere else, redirect
  if (location.pathname !== initialTarget && !exitState) {
    // if exitState is not yet present, honor the initial target
    return <Navigate to={initialTarget} replace />;
  }

  // otherwise render children — subsequent updates are handled by the effect above
  return children;
}
